import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Star } from 'lucide-react';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Color Analysis Card
 * -------------------
 * Sits in the Dashboard left rail, below the Fit selector.
 * - Auto-loads cached analysis on mount via POST /api/color-analysis (force=false)
 * - First-time loads trigger a fresh Gemini analysis; subsequent loads return
 *   the cached palette stored on the client document — the analysis is treated
 *   as the SINGLE SOURCE OF TRUTH (no re-analyse button) so the consultant
 *   never gets a different answer twice for the same person.
 * - Exposes `bestColors` upward via onAnalysis so the parent can star-mark
 *   any matching suit colours.
 */
export default function ColorAnalysisCard({ clientId, onAnalysis }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter 172 — count consecutive retry attempts so the fallback copy
  // can show "Attempt 2 / 3" progress and prove to the tailor that the
  // Retry button IS firing on every click (user complaint: after adding
  // Universal Key credit, clicks "did nothing" — actually the LLM was
  // still returning the same fallback, but the UI looked frozen).
  const [retryCount, setRetryCount] = useState(0);
  const autoRetryRef = React.useRef(false);

  const load = useCallback(async (force = false) => {
    if (!clientId) return;
    setLoading(true);
    if (force) setRetryCount((c) => c + 1);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/color-analysis`,
        { client_id: clientId, force },
        // Iter 172 — bumped from 45s → 90s. The two Gemini paths (direct
        // + Emergent router with a 0.5s retry) can legitimately take
        // ~50-70s on cold start; the shorter timeout caused axios to
        // abort while the backend was still processing, which the
        // frontend rendered as "retry doesn't work" even though the
        // server was working.
        { withCredentials: true, timeout: 90000 }
      );
      setAnalysis(data.analysis);
      onAnalysis?.(data.analysis);
      // Retry-success feedback — the tailor sees a green toast the
      // instant the personalised palette lands, so a click that WORKS
      // never looks like a click that failed.
      if (force && data?.analysis && data.analysis.is_fallback !== true) {
        toast.success(`Personalised palette ready — ${data.analysis.season || 'analysis complete'}`);
        setRetryCount(0);
      } else if (force && data?.analysis?.is_fallback === true) {
        // Retry ran but the service still couldn't respond — give the
        // tailor an explicit signal so they know the click DID hit the
        // backend even when the palette itself hasn't changed.
        toast.error('Colour analysis still unavailable — please try again shortly.');
      }
      if (
        data?.analysis?.is_fallback === true
        && !force
        && !autoRetryRef.current
      ) {
        autoRetryRef.current = true;
        setTimeout(() => { load(true); }, 2500);
      }
    } catch (e) {
      // Iter 172 — no more silent catch. If the network call itself
      // errored (500 / timeout / offline), tell the tailor so they
      // know to check their connection or contact support.
      if (force) {
        const msg = e?.response?.data?.detail || e?.message || 'Network error';
        toast.error(`Retry failed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, onAnalysis]);

  // Auto-load cached analysis once per client mount
  useEffect(() => {
    setAnalysis(null);
    setRetryCount(0);
    autoRetryRef.current = false;
    if (clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (!clientId) return null;

  const isFallback = analysis?.is_fallback === true;

  // Iter 172 — map backend `fallback_reason` to actionable copy.
  const fallbackCopy = (() => {
    if (!isFallback) return null;
    const reason = analysis?.fallback_reason;
    if (reason === 'photo_fetch_failed' && analysis?.summary) return analysis.summary;
    if (reason === 'llm_failed') {
      return analysis?.summary || (
        'Our colour analysis service is temporarily unavailable. Please try again in a moment — '
        + 'if this persists, contact support.'
      );
    }
    return "We couldn't complete the personalised analysis just now. Your last saved analysis (if any) is still in place; tap Retry to run a fresh analysis.";
  })();

  return (
    <div className="bg-[#121215] border border-[#27272A] p-5" data-testid="color-analysis-card">
      <div className="flex items-center justify-between mb-3">
        <Label className="label-luxury">Color Analysis</Label>
      </div>

      {loading && !analysis ? (
        <div className="flex items-center gap-2 text-xs text-[#52525B] py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Analysing skin tone & build…</span>
        </div>
      ) : isFallback ? (
        <div className="space-y-2" data-testid="color-analysis-fallback">
          <p className="text-[11px] text-[#A1A1AA] leading-snug">
            {fallbackCopy}
          </p>
          {retryCount > 0 && (
            <p className="text-[10px] text-[#52525B] leading-snug" data-testid="color-analysis-retry-count">
              Retry attempts: {retryCount}
              {loading ? ' · in progress…' : ' · service still unavailable'}
            </p>
          )}
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="w-full h-8 text-[10px] uppercase tracking-widest font-semibold border border-[#C2A878] text-[#C2A878] hover:bg-[#C2A878]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="color-analysis-retry"
          >
            {loading ? 'Retrying…' : (retryCount > 0 ? 'Retry again' : 'Retry analysis')}
          </button>
        </div>
      ) : analysis ? (
        <>
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-widest text-[#C2A878] mb-1">
              {/* Iter 168 — no more silent "Classic Menswear" default.
                  Real analyses carry a specific season; if we ever land
                  here without one it's a bug that must be visible. */}
              {analysis.season || 'Personalised palette pending'}
            </p>
            {analysis.summary && (
              <p className="text-[11px] text-[#A1A1AA] leading-snug">
                {analysis.summary}
              </p>
            )}
          </div>

          <div className="grid grid-cols-6 gap-1.5 mb-1" data-testid="color-best-grid">
            {(analysis.best_colors || []).map((c, i) => (
              <div
                key={i}
                className="aspect-square border border-[#27272A] relative group cursor-help"
                style={{ backgroundColor: c.hex }}
                title={`${c.name} · ${c.hex}`}
              >
                <Star className="w-2.5 h-2.5 absolute top-0.5 right-0.5 text-[#C2A878]" fill="#C2A878" />
              </div>
            ))}
          </div>
          <p className="text-[9px] uppercase tracking-widest text-[#52525B]">Best for them</p>
        </>
      ) : null}
    </div>
  );
}

// ─── helper: is a given hex within tolerance of any best_color? ───────
// Exposed so the customizer's color swatches can star-mark matching colours.
export function buildColorMatcher(analysis) {
  const bestList = analysis?.best_colors || [];
  if (!bestList.length) return () => false;

  const targets = bestList
    .map(c => (c?.hex || '').replace('#', ''))
    .filter(h => /^[0-9a-fA-F]{6}$/.test(h))
    .map(h => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]);

  // Simple RGB distance (Euclidean) under 80 ≈ visually similar
  const TOL = 80;
  return (hex) => {
    if (!hex || typeof hex !== 'string') return false;
    const h = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return false;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return targets.some(([tr, tg, tb]) => {
      const d = Math.sqrt((r - tr) ** 2 + (g - tg) ** 2 + (b - tb) ** 2);
      return d <= TOL;
    });
  };
}

// Helper export so the matcher can be memoized in the parent
export function useColorMatcher(analysis) {
  return useMemo(() => buildColorMatcher(analysis), [analysis]);
}
